import {
  applyBillDiscount,
  autoRoundOff,
  BASE_UNIT_LABELS,
  round2,
} from "@medicorepay/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "../../../lib/toast";
import { Customer, customerService } from "../../../services/customer.service";
import {
  Product,
  ProductPack,
  productService,
} from "../../../services/product.service";
import { useAuthStore } from "../../../stores/authStore";

export interface CartItem {
  product: Product;
  quantity: number;
  batchId?: string;
  batchStock: number; // available stock in base units
  discountType: "AMOUNT" | "PERCENT";
  discountValue: number;
  rate: number;
  gstPercent: number;
  gstAmount: number;
  total: number;
  // Pack/UOM support
  salePackId?: string;
  unitsPerPack: number;
  packLabel: string;
  availablePacks: ProductPack[];
}

export interface POSSession {
  id: string;
  name: string;
  cart: CartItem[];
  selectedCustomer: Customer | null;
  uploadedPrescription: any | null;
  scheduledDrugDoctorName: string;
  scheduledDrugPrescribedDate: string;
  notes: string;
  billDiscountType: "AMOUNT" | "PERCENT";
  billDiscountValue: string;
  roundOffAmount: string;
  paymentMethod: string;
  amountTendered: string;
  doctorName: string;
  doctorRegistration: string;
  hospitalName: string;
  isReverseCharge: boolean;
  reverseChargeReason: string;
}

const createEmptySession = (name: string): POSSession => ({
  id: Date.now().toString(),
  name,
  cart: [],
  selectedCustomer: null,
  uploadedPrescription: null,
  scheduledDrugDoctorName: "",
  scheduledDrugPrescribedDate: "",
  notes: "",
  billDiscountType: "AMOUNT",
  billDiscountValue: "",
  roundOffAmount: "",
  paymentMethod: "CASH",
  amountTendered: "",
  doctorName: "",
  doctorRegistration: "",
  hospitalName: "",
  isReverseCharge: false,
  reverseChargeReason: "",
});

interface DiscountEngine {
  calculateDiscountAmount: (
    lineTotal: number,
    discountType: "AMOUNT" | "PERCENT",
    discountValue: number,
  ) => number;
  calculateSchemeDiscount: (
    product: Product,
    lineTotal: number,
    quantity: number,
  ) => number;
  calculateSchemeFreeQuantity: (
    product: Product,
    lineTotal: number,
    quantity: number,
  ) => number;
}

interface AddToCartOptions {
  quantity?: number;
  batchId?: string;
  batchStock?: number;
  rate?: number;
}

function getBaseUnitLabel(product: Product): string {
  const baseUnitType = product.baseUnitType?.toUpperCase() as
    | keyof typeof BASE_UNIT_LABELS
    | undefined;

  return (
    (baseUnitType ? BASE_UNIT_LABELS[baseUnitType] : undefined) ||
    product.unit ||
    "Unit"
  );
}

function calculateInclusiveAmounts(
  grossAfterDiscount: number,
  gstPercent: number,
) {
  const payablePaise = Math.max(0, Math.round(grossAfterDiscount * 100));
  const gstBps = Math.max(0, Math.round(gstPercent * 100));
  const taxablePaise = gstBps
    ? Math.round((payablePaise * 10_000) / (10_000 + gstBps))
    : payablePaise;

  return {
    taxableAmount: taxablePaise / 100,
    gstAmount: (payablePaise - taxablePaise) / 100,
    total: payablePaise / 100,
  };
}

export function usePOSCart(discountEngine: DiscountEngine, storeId?: string) {
  const searchRef = useRef<HTMLInputElement>(null);
  const isCompositeDealer = useAuthStore(
    (state) => !!state.user?.isCompositeDealer,
  );

  // Session state
  const [sessions, setSessions] = useState<POSSession[]>([
    createEmptySession("Cart 1"),
  ]);
  const [activeSessionId, setActiveSessionId] = useState(sessions[0].id);
  const nextCartNumber = useRef(2);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searching, setSearching] = useState(false);

  // Customer search state
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState<Customer[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");

  // UI state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const resolveFefoBatchPricing = useCallback(
    async (product: Product) => {
      try {
        const batches = await productService.getBatches({
          productId: product.id,
          storeId,
          inStock: true,
        });

        const fefoBatch = (batches || []).find(
          (batch: any) => Number(batch.quantity || 0) > 0,
        );

        if (!fefoBatch) {
          return {
            batchId: undefined as string | undefined,
            rate: Number(product.sellingPrice),
            batchStock: 0,
          };
        }

        // Detect if batch prices are per-pack (legacy) or per-base-unit (new)
        // If batch was received in packs, check receivedPack.unitsPerPack.
        // Legacy batches stored pack-level prices; new GRNs store per-unit prices.
        // Heuristic: if receivedPack exists and batch.costPrice > landedCost × threshold,
        // the prices are still at pack level (legacy)
        const receivedPackUnits =
          (fefoBatch as any).receivedPack?.unitsPerPack || 1;
        const landedCost = Number((fefoBatch as any).landedCost || 0);
        const rawCostPrice = Number(fefoBatch.costPrice || 0);
        // If costPrice is much larger than landedCost and we have a multi-unit pack,
        // the batch prices are still at pack level (legacy)
        const needsConversion =
          receivedPackUnits > 1 &&
          landedCost > 0 &&
          rawCostPrice > 0 &&
          rawCostPrice > landedCost * 1.5;
        const divisor = needsConversion ? receivedPackUnits : 1;

        const batchMrp = Number(fefoBatch.mrp || 0) / divisor;
        const batchSelling = Number(fefoBatch.sellingPrice || 0) / divisor;
        const productSelling = Number(product.sellingPrice || 0);

        const effectiveSelling =
          batchSelling > 0 ? batchSelling : productSelling;
        const effectiveRate =
          batchMrp > 0
            ? Math.min(effectiveSelling || productSelling, batchMrp)
            : effectiveSelling || productSelling;

        return {
          batchId: fefoBatch.id as string,
          rate: Number(effectiveRate.toFixed(2)),
          batchStock: Number(fefoBatch.quantity || 0),
        };
      } catch {
        return {
          batchId: undefined as string | undefined,
          rate: Number(product.sellingPrice),
          batchStock: 0,
        };
      }
    },
    [storeId],
  );

  const activeSession =
    sessions.find((s) => s.id === activeSessionId) || sessions[0];

  const updateActiveSession = useCallback(
    (update: Partial<POSSession>) => {
      setSessions((prev) =>
        prev.map((s) => (s.id === activeSessionId ? { ...s, ...update } : s)),
      );
    },
    [activeSessionId],
  );

  // Session management
  const addSession = useCallback(() => {
    const newSession = createEmptySession(`Cart ${nextCartNumber.current++}`);
    setSessions((current) => [...current, newSession]);
    setActiveSessionId(newSession.id);
  }, []);

  const removeSession = useCallback(
    (id: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (sessions.length === 1) {
        const fresh = createEmptySession(`Cart ${nextCartNumber.current++}`);
        setSessions([fresh]);
        setActiveSessionId(fresh.id);
        return;
      }
      const newSessions = sessions.filter((s) => s.id !== id);
      setSessions(newSessions);
      if (activeSessionId === id) {
        setActiveSessionId(newSessions[newSessions.length - 1].id);
      }
    },
    [sessions, activeSessionId],
  );

  // Convenience destructuring
  const {
    cart,
    selectedCustomer,
    uploadedPrescription,
    scheduledDrugDoctorName,
    scheduledDrugPrescribedDate,
    notes,
    billDiscountType,
    billDiscountValue,
    roundOffAmount,
    paymentMethod,
    amountTendered,
    doctorName,
    doctorRegistration,
    hospitalName,
    isReverseCharge,
    reverseChargeReason,
  } = activeSession;

  const setCart = (cart: CartItem[]) => updateActiveSession({ cart });
  const setSelectedCustomer = (selectedCustomer: Customer | null) =>
    updateActiveSession({ selectedCustomer });
  const setUploadedPrescription = (uploadedPrescription: any) =>
    updateActiveSession({ uploadedPrescription });
  const setScheduledDrugDoctorName = (scheduledDrugDoctorName: string) =>
    updateActiveSession({ scheduledDrugDoctorName });
  const setScheduledDrugPrescribedDate = (
    scheduledDrugPrescribedDate: string,
  ) => updateActiveSession({ scheduledDrugPrescribedDate });
  const setNotes = (notes: string) => updateActiveSession({ notes });
  const setBillDiscountType = (billDiscountType: "AMOUNT" | "PERCENT") =>
    updateActiveSession({ billDiscountType });
  const setBillDiscountValue = (billDiscountValue: string) =>
    updateActiveSession({ billDiscountValue });
  const setRoundOffAmount = (roundOffAmount: string) =>
    updateActiveSession({ roundOffAmount });
  const setPaymentMethod = (paymentMethod: string) =>
    updateActiveSession({ paymentMethod });
  const setAmountTendered = (amountTendered: string) =>
    updateActiveSession({ amountTendered });
  const setDoctorName = (doctorName: string) =>
    updateActiveSession({ doctorName });
  const setDoctorRegistration = (doctorRegistration: string) =>
    updateActiveSession({ doctorRegistration });
  const setHospitalName = (hospitalName: string) =>
    updateActiveSession({ hospitalName });
  const setIsReverseCharge = (isReverseCharge: boolean) =>
    updateActiveSession({ isReverseCharge });
  const setReverseChargeReason = (reverseChargeReason: string) =>
    updateActiveSession({ reverseChargeReason });

  // Product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchProducts();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2 && !selectedCustomer) {
        searchCustomers();
      } else {
        setCustomerResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const searchProducts = async () => {
    try {
      setSearching(true);

      // --- G9: Barcode-based POS quick-add ---
      // If the search term looks like a barcode (all digits, 8-14 chars),
      // attempt direct barcode lookup first for instant add-to-cart.
      const trimmed = searchTerm.trim();
      if (/^\d{8,14}$/.test(trimmed)) {
        try {
          const product = await productService.getProductByBarcode(trimmed);
          if (product && (product.totalStock || 0) > 0) {
            addToCart(product);
            setSearchTerm("");
            setSearchResults([]);
            searchRef.current?.focus();
            return;
          }
        } catch {
          // Barcode not found — fall through to regular search
        }
      }

      const response = await productService.getProducts({
        search: searchTerm,
        limit: 10,
        status: "active",
      });
      setSearchResults(
        response.data.filter((p: Product) => (p.totalStock || 0) > 0),
      );
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setSearching(false);
    }
  };

  const searchCustomers = async () => {
    try {
      setSearchingCustomer(true);
      setShowCustomerDropdown(true);
      const response = await customerService.getCustomers({
        search: customerSearch,
        limit: 5,
      });
      setCustomerResults(response.data);
    } catch (err) {
      console.error("Customer search failed", err);
    } finally {
      setSearchingCustomer(false);
    }
  };

  const selectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setCustomerResults([]);
    setShowCustomerDropdown(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setIsCreatingCustomer(false);
    setNewName("");
    setNewPhone("");
    setIsReverseCharge(false);
    setReverseChargeReason("");
  };

  const handleQuickCreate = async () => {
    if (!newName || !newPhone) {
      showToast.error("Name and Phone are required");
      return;
    }
    try {
      setProcessing(true);
      const newCustomer = await customerService.createCustomer({
        name: newName,
        phone: newPhone,
      });
      selectCustomer(newCustomer);
      showToast.success("Customer created successfully");
      setIsCreatingCustomer(false);
      setNewName("");
      setNewPhone("");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to create customer",
      );
    } finally {
      setProcessing(false);
    }
  };

  // Cart calculation helpers
  const getEffectiveGstPercent = useCallback(
    (product: Product) => {
      if (isCompositeDealer || product.isGstExempted) {
        return 0;
      }

      return Number(product.gstPercent || 0);
    },
    [isCompositeDealer],
  );

  const calculateItemTotals = (
    product: Product,
    quantity: number,
    discountType: "AMOUNT" | "PERCENT" = "AMOUNT",
    discountValue: number = 0,
    rate: number = Number(product.sellingPrice),
  ): { gstAmount: number; total: number } => {
    const lineTotal = rate * quantity;
    const discountAmount = discountEngine.calculateDiscountAmount(
      lineTotal,
      discountType,
      discountValue,
    );
    const schemeDiscount = discountEngine.calculateSchemeDiscount(
      product,
      lineTotal,
      quantity,
    );
    const discountedAmount = lineTotal - discountAmount - schemeDiscount;
    const gstPercent = getEffectiveGstPercent(product);
    const { gstAmount, total } = calculateInclusiveAmounts(
      discountedAmount,
      gstPercent,
    );

    return {
      gstAmount: Number(gstAmount.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  };

  const addToCart = async (product: Product, options?: AddToCartOptions) => {
    const requestedQty = Math.max(1, options?.quantity || 1);
    const existingIndex = cart.findIndex(
      (item) => item.product.id === product.id,
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      const existingItem = newCart[existingIndex];
      const newQuantity = existingItem.quantity + requestedQty;

      // Hard stock validation — block overselling
      const baseQtyNeeded = newQuantity * existingItem.unitsPerPack;
      if (
        existingItem.batchStock > 0 &&
        baseQtyNeeded > existingItem.batchStock
      ) {
        const maxPackQty = Math.floor(
          existingItem.batchStock / existingItem.unitsPerPack,
        );
        showToast.error(
          `Insufficient stock: only ${maxPackQty} ${existingItem.packLabel}(s) available for ${product.name}`,
        );
        return;
      }

      const totals = calculateItemTotals(
        product,
        newQuantity,
        existingItem.discountType,
        existingItem.discountValue,
        existingItem.rate,
      );
      newCart[existingIndex] = {
        ...existingItem,
        quantity: newQuantity,
        gstAmount: totals.gstAmount,
        total: totals.total,
      };
      setCart(newCart);
    } else {
      const autoPricing = await resolveFefoBatchPricing(product);
      const batchId = options?.batchId ?? autoPricing.batchId;
      const batchStock = options?.batchStock ?? autoPricing.batchStock;
      const rate = options?.rate ?? autoPricing.rate;

      // Load available sale packs for this product
      let availablePacks: ProductPack[] = [];
      try {
        const packs = await productService.getProductPacks(product.id);
        availablePacks = packs.filter((p) => p.isSaleUnit && p.isActive);
      } catch {
        // Product may not have packs — use base unit
      }

      // Start on the base unit. Packs remain available in the dropdown.
      const effectiveRate = rate;

      const totals = calculateItemTotals(
        product,
        requestedQty,
        "AMOUNT",
        0,
        effectiveRate,
      );
      const cartItem: CartItem = {
        product,
        quantity: requestedQty,
        batchId,
        batchStock,
        discountType: "AMOUNT",
        discountValue: 0,
        rate: effectiveRate,
        gstPercent: getEffectiveGstPercent(product),
        gstAmount: totals.gstAmount,
        total: totals.total,
        salePackId: undefined,
        unitsPerPack: 1,
        packLabel: getBaseUnitLabel(product),
        availablePacks,
      };
      setCart([...cart, cartItem]);
    }

    setSearchTerm("");
    setSearchResults([]);
    searchRef.current?.focus();
  };

  const updateQuantity = (index: number, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(index);
      return;
    }
    const newCart = [...cart];
    const item = newCart[index];

    // Hard stock validation — cap at available stock
    const baseQtyNeeded = quantity * item.unitsPerPack;
    if (item.batchStock > 0 && baseQtyNeeded > item.batchStock) {
      const maxPackQty = Math.floor(item.batchStock / item.unitsPerPack);
      quantity = Math.max(1, maxPackQty);
      showToast.error(
        `Insufficient stock: capped to ${quantity} ${item.packLabel}(s) for ${item.product.name}`,
      );
    }

    const totals = calculateItemTotals(
      item.product,
      quantity,
      item.discountType,
      item.discountValue,
      item.rate,
    );
    newCart[index] = {
      ...item,
      quantity,
      gstAmount: totals.gstAmount,
      total: totals.total,
    };
    setCart(newCart);
  };

  const updateDiscount = (index: number, discountValue: number) => {
    const newCart = [...cart];
    const totals = calculateItemTotals(
      newCart[index].product,
      newCart[index].quantity,
      newCart[index].discountType,
      discountValue,
      newCart[index].rate,
    );
    newCart[index] = {
      ...newCart[index],
      discountValue,
      gstAmount: totals.gstAmount,
      total: totals.total,
    };
    setCart(newCart);
  };

  const updateDiscountType = (
    index: number,
    discountType: "AMOUNT" | "PERCENT",
  ) => {
    const newCart = [...cart];
    const totals = calculateItemTotals(
      newCart[index].product,
      newCart[index].quantity,
      discountType,
      newCart[index].discountValue,
      newCart[index].rate,
    );
    newCart[index] = {
      ...newCart[index],
      discountType,
      gstAmount: totals.gstAmount,
      total: totals.total,
    };
    setCart(newCart);
  };

  /**
   * P4.1: Update the sale pack/UOM for a cart item.
   * Passing packId=undefined selects base unit.
   */
  const updatePack = (index: number, packId: string | undefined) => {
    const newCart = [...cart];
    const item = newCart[index];
    const pack = packId
      ? item.availablePacks.find((p) => p.id === packId)
      : undefined;

    const newRate = pack
      ? Number(pack.sellingPrice) || item.rate
      : Number(item.product.sellingPrice) || item.rate;
    const newUnitsPerPack = pack
      ? pack.resolvedBaseUnitsPerPack || pack.unitsPerPack
      : 1;
    const newPackLabel = pack
      ? pack.packDescription
      : getBaseUnitLabel(item.product);

    // Hard stock validation when changing pack size
    let adjustedQuantity = item.quantity;
    const baseQtyNeeded = item.quantity * newUnitsPerPack;
    if (item.batchStock > 0 && baseQtyNeeded > item.batchStock) {
      const maxPackQty = Math.max(
        1,
        Math.floor(item.batchStock / newUnitsPerPack),
      );
      adjustedQuantity = maxPackQty;
      showToast.error(
        `Insufficient stock: capped to ${maxPackQty} ${newPackLabel}(s) for ${item.product.name}`,
      );
    }

    const totals = calculateItemTotals(
      item.product,
      adjustedQuantity,
      item.discountType,
      item.discountValue,
      newRate,
    );

    newCart[index] = {
      ...item,
      quantity: adjustedQuantity,
      salePackId: packId,
      unitsPerPack: newUnitsPerPack,
      packLabel: newPackLabel,
      rate: newRate,
      gstAmount: totals.gstAmount,
      total: totals.total,
    };
    setCart(newCart);
  };

  const removeFromCart = (index: number) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const clearCart = () => {
    setCart([]);
    setSearchTerm("");
    clearCustomer();
    setNotes("");
    setRoundOffAmount("");
    setUploadedPrescription(null);
    setScheduledDrugDoctorName("");
    setScheduledDrugPrescribedDate("");
    setDoctorName("");
    setHospitalName("");
    setIsReverseCharge(false);
    setReverseChargeReason("");
  };

  const hasScheduledDrugs = () => {
    return cart.some((item) =>
      ["SCHEDULE_H", "SCHEDULE_H1", "SCHEDULE_X"].includes(
        item.product.scheduleType || "",
      ),
    );
  };

  // Computed cart with discount details
  const computedCart = cart.map((item) => {
    const lineTotal = item.rate * item.quantity;
    const manualDiscount = discountEngine.calculateDiscountAmount(
      lineTotal,
      item.discountType,
      item.discountValue,
    );
    const schemeDiscount = discountEngine.calculateSchemeDiscount(
      item.product,
      lineTotal,
      item.quantity,
    );
    const schemeFreeQuantity = discountEngine.calculateSchemeFreeQuantity(
      item.product,
      lineTotal,
      item.quantity,
    );
    const grossAfterDiscount = lineTotal - manualDiscount - schemeDiscount;
    const effectiveGstPercent = getEffectiveGstPercent(item.product);
    const { taxableAmount, gstAmount, total } = calculateInclusiveAmounts(
      grossAfterDiscount,
      effectiveGstPercent,
    );

    return {
      ...item,
      gstPercent: effectiveGstPercent,
      manualDiscount,
      schemeDiscount,
      schemeFreeQuantity,
      taxableAmount,
      gstAmount: Number(gstAmount.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  });

  const calculateTotals = () => {
    const subtotal = computedCart.reduce(
      (sum, item) => sum + item.taxableAmount,
      0,
    );

    const totalGst = computedCart.reduce(
      (sum, item) => sum + item.gstAmount,
      0,
    );
    const grossTotal = subtotal + totalGst;

    // Bill discount is a post-GST cash discount applied once to the gross
    // total. Pass the entered value to the shared calculator so percentage
    // discounts are not converted and then applied a second time.
    const { billDiscountAmount, baseGrandTotal } = applyBillDiscount(
      subtotal,
      totalGst,
      billDiscountType === "PERCENT" ? "PERCENTAGE" : "FLAT",
      parseFloat(billDiscountValue) || 0,
    );
    // Auto round-off to nearest ₹1 unless user has manually entered a value
    const effectiveRoundOff =
      roundOffAmount === ""
        ? autoRoundOff(baseGrandTotal)
        : parseFloat(roundOffAmount) || 0;
    const grandTotal = Math.max(0, round2(baseGrandTotal + effectiveRoundOff));
    const manualDiscountTotal = computedCart.reduce(
      (sum, item) => sum + item.manualDiscount,
      0,
    );
    const schemeDiscountTotal = computedCart.reduce(
      (sum, item) => sum + item.schemeDiscount,
      0,
    );

    return {
      subtotal: Number(subtotal.toFixed(2)),
      totalGst: Number(totalGst.toFixed(2)),
      grossTotal: Number(grossTotal.toFixed(2)),
      grandTotal: Number(grandTotal.toFixed(2)),
      roundOffAmount: Number(effectiveRoundOff.toFixed(2)),
      manualDiscountTotal: Number(manualDiscountTotal.toFixed(2)),
      schemeDiscountTotal: Number(schemeDiscountTotal.toFixed(2)),
      billDiscountAmount: Number(billDiscountAmount.toFixed(2)),
    };
  };

  return {
    // Refs
    searchRef,
    // Sessions
    sessions,
    activeSessionId,
    setActiveSessionId,
    addSession,
    removeSession,
    // Cart state
    cart,
    computedCart,
    // Cart actions
    addToCart,
    updateQuantity,
    updateDiscount,
    updateDiscountType,
    updatePack,
    removeFromCart,
    clearCart,
    // Cart calculations
    calculateTotals,
    hasScheduledDrugs,
    // Customer
    selectedCustomer,
    customerSearch,
    setCustomerSearch,
    customerResults,
    showCustomerDropdown,
    setShowCustomerDropdown,
    searchingCustomer,
    isCreatingCustomer,
    setIsCreatingCustomer,
    newName,
    setNewName,
    newPhone,
    setNewPhone,
    selectCustomer,
    clearCustomer,
    handleQuickCreate,
    setSelectedCustomer,
    // Product search
    searchTerm,
    setSearchTerm,
    searchResults,
    searching,
    // Form state
    notes,
    setNotes,
    billDiscountType,
    setBillDiscountType,
    billDiscountValue,
    setBillDiscountValue,
    roundOffAmount,
    setRoundOffAmount,
    paymentMethod,
    setPaymentMethod,
    amountTendered,
    setAmountTendered,
    doctorName,
    setDoctorName,
    doctorRegistration,
    setDoctorRegistration,
    hospitalName,
    setHospitalName,
    isReverseCharge,
    setIsReverseCharge,
    reverseChargeReason,
    setReverseChargeReason,
    uploadedPrescription,
    setUploadedPrescription,
    scheduledDrugDoctorName,
    setScheduledDrugDoctorName,
    scheduledDrugPrescribedDate,
    setScheduledDrugPrescribedDate,
    // UI state
    processing,
    setProcessing,
    error,
    setError,
  };
}
